/**
 * Embed an XMP packet into a PNG by inserting an `iTXt` chunk (keyword
 * `XML:com.adobe.xmp`) immediately before the terminating `IEND` chunk. This is
 * pure byte manipulation — no re-encode, no native deps — so it runs identically
 * in the browser (static-ad download/launch) and in Node.
 *
 * PNG structure: 8-byte signature, then a sequence of chunks, each
 * `length(4 BE) + type(4) + data(length) + crc(4)`, ending with `IEND`.
 */

const PNG_SIGNATURE = [137, 80, 78, 71, 13, 10, 26, 10];
const XMP_KEYWORD = "XML:com.adobe.xmp";

export function injectXmpIntoPng(png: Uint8Array, xmp: string): Uint8Array {
  assertPngSignature(png);
  const chunk = buildITxtChunk(XMP_KEYWORD, xmp);
  const iendStart = findIendChunkStart(png);

  const out = new Uint8Array(png.length + chunk.length);
  out.set(png.subarray(0, iendStart), 0);
  out.set(chunk, iendStart);
  out.set(png.subarray(iendStart), iendStart + chunk.length);
  return out;
}

function assertPngSignature(png: Uint8Array): void {
  if (png.length < PNG_SIGNATURE.length) {
    throw new Error("injectXmpIntoPng: input is not a PNG (too short)");
  }
  const view = dataView(png);
  for (let i = 0; i < PNG_SIGNATURE.length; i++) {
    if (view.getUint8(i) !== PNG_SIGNATURE[i]) {
      throw new Error("injectXmpIntoPng: input is not a PNG (bad signature)");
    }
  }
}

function findIendChunkStart(png: Uint8Array): number {
  const view = dataView(png);
  let offset = PNG_SIGNATURE.length;
  while (offset + 8 <= png.length) {
    const length = view.getUint32(offset);
    const type = String.fromCharCode(
      view.getUint8(offset + 4),
      view.getUint8(offset + 5),
      view.getUint8(offset + 6),
      view.getUint8(offset + 7),
    );
    if (type === "IEND") {
      return offset;
    }
    // Advance past length(4) + type(4) + data(length) + crc(4).
    offset += 12 + length;
  }
  throw new Error("injectXmpIntoPng: no IEND chunk found");
}

function buildITxtChunk(keyword: string, text: string): Uint8Array {
  const encoder = new TextEncoder();
  const keywordBytes = encoder.encode(keyword);
  const textBytes = encoder.encode(text);

  // iTXt data layout:
  //   keyword + 0x00 (separator)
  //   compression flag (0) + compression method (0)
  //   language tag (empty) + 0x00
  //   translated keyword (empty) + 0x00
  //   text (UTF-8)
  // The five bytes between keyword and text are all zero, which the
  // zero-initialized Uint8Array already provides.
  const data = new Uint8Array(keywordBytes.length + 5 + textBytes.length);
  data.set(keywordBytes, 0);
  data.set(textBytes, keywordBytes.length + 5);

  const typeBytes = encoder.encode("iTXt");
  const chunk = new Uint8Array(12 + data.length);
  const view = dataView(chunk);
  view.setUint32(0, data.length);
  chunk.set(typeBytes, 4);
  chunk.set(data, 8);

  // CRC is computed over the chunk type followed by the chunk data.
  const crcInput = new Uint8Array(typeBytes.length + data.length);
  crcInput.set(typeBytes, 0);
  crcInput.set(data, typeBytes.length);
  view.setUint32(8 + data.length, crc32(crcInput));
  return chunk;
}

function dataView(bytes: Uint8Array): DataView {
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
}

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc ^= byte;
    for (let k = 0; k < 8; k++) {
      crc = crc & 1 ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1;
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
}
